import { MantineProvider } from '@mantine/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { RouterProvider, createBrowserRouter, createMemoryRouter } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import type { AuthSource } from './auth/types';
import { MetricsProvider } from './data/MetricsProvider';
import type { MetricsSubscribe } from './data/metricsContext';
import { FleetProvider } from './fleet/FleetProvider';
import type { FleetSubscribe } from './fleet/fleetContext';
import { queryClient } from './query/queryClient';
import { appRoutes } from './routes/routes';
import { browserRouterBasename } from './routes/routerBase';
import { theme } from './theme/theme';
import { AdminWorkspaceFactoryContext } from './workspace/context';
import { createAdminWorkspace, type AdminWorkspace } from './workspace/gateway';

interface AppProps {
  /** Puerto de autenticación (Firebase en prod, fake en tests). */
  authSource: AuthSource;
  /** Si se pasa, usa un router en memoria (tests). Si no, browser router. */
  initialEntries?: string[];
  /** Puerto legacy conservado para consumidores y pruebas de compatibilidad. El inicio usa KMP. */
  metricsSubscribe?: MetricsSubscribe;
  /** Puerto legacy conservado; el mapa de inicio consulta la flota gestionada mediante KMP. */
  fleetSubscribe?: FleetSubscribe;
  workspaceFactory?: (owner: string) => Promise<AdminWorkspace>;
}

export function App({
  authSource,
  initialEntries,
  metricsSubscribe,
  fleetSubscribe,
  workspaceFactory = createAdminWorkspace,
}: AppProps): ReactNode {
  const router = useMemo(
    () =>
      initialEntries
        ? createMemoryRouter(appRoutes, { initialEntries })
        : createBrowserRouter(appRoutes, {
            basename: browserRouterBasename(import.meta.env.BASE_URL),
          }),
    [initialEntries],
  );

  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <QueryClientProvider client={queryClient}>
        <AuthProvider source={authSource}>
          <MetricsProvider subscribe={metricsSubscribe}>
            <FleetProvider subscribe={fleetSubscribe}>
              <AdminWorkspaceFactoryContext.Provider value={workspaceFactory}><RouterProvider router={router} /></AdminWorkspaceFactoryContext.Provider>
            </FleetProvider>
          </MetricsProvider>
        </AuthProvider>
      </QueryClientProvider>
    </MantineProvider>
  );
}
